// Dither and 2-bit look (2026-10-30, docs/plans/dither-2bit.md): a room with rooms.json `look` draws its surfaces in
// a few colours with ordered dithering and big square pixels.
//
// Two parts, chosen by `look.mode`:
//  - texture: a patch on the room materials (after the reflection layer and the room grade). The baked atlas is read
//    once per cell, a square of `cell_m` metres on the surface laid out in the atlas's own UVs (the cell's average
//    colour: textureLod at the cell's size), then the colour is quantised to the palette with a dither threshold that
//    belongs to the cell. The pattern is fixed to the surfaces, so it does not swim when the camera moves. Where a
//    cell would be smaller than `min_px` screen pixels, cells double in size (a power-of-two grid, so the larger cells
//    hold whole smaller ones). Materials without a texture use a world-space grid on the face's dominant plane.
//    No extra pass, no extra texture; a few ALU ops and one fwidth per fragment.
//  - screen: the scene is drawn into a small render target (`screen_px` pixels high), then a full-screen pass scales it
//    up with nearest filtering and quantises each low-res pixel with a screen-space threshold (this pattern stays on
//    the screen while the room moves under it).
//  - hybrid: texture, plus the low-res pass without its own quantisation.
//
// Colour (`look.color`, docs/plans/dither-2bit.md, Colour): ramp maps luminance to 4 colours in order (monochrome);
// channels dithers R, G and B separately to 2^bits levels; nearest dithers between the two palette colours nearest in
// sRGB (in screen mode it runs in the low-res image, then a second pass scales that up). `saturation` is applied first.
// mix: each pixel takes the nearest result or the channels result, chosen by the threshold against `mix`; `pull`
// (channels) moves the colour toward the nearest palette colour, keeping its luminance, before quantising
// (docs/plans/dither-2bit.md, Mixing PICO-8 and 3-3-2).
//
// Rooms without `look` are not touched: their materials are patched only once a room with a look is shown, and the
// patch is a no-op (same shader text) while the look is off.

import {
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  SRGBColorSpace,
  Material,
  Vector2,
  Vector3,
  WebGLRenderTarget,
  Color,
  DataTexture,
  FloatType,
  RedFormat,
  UnsignedByteType,
  type Camera,
  type Texture,
  type WebGLRenderer,
} from 'three';
import type { LoadedScene } from '../scene/loader';
import { blueNoise64 } from './blue-noise';

export const DITHER_KINDS = ['bayer2', 'bayer4', 'bayer8', 'r2', 'blue', 'white', 'ign', 'halftone', 'hatch', 'streak', 'lines', 'rowdiff', 'none'] as const;
export type DitherKind = (typeof DITHER_KINDS)[number];
/** Kinds that can change per frame (`dither_fps`); the ordered ones (Bayer, halftone, hatch) cannot. */
export const ANIMATABLE: readonly DitherKind[] = ['r2', 'blue', 'white', 'ign', 'streak', 'rowdiff'];
/** Kinds that use the row noise (`streak`, `row_jitter`). */
const STREAKY: readonly DitherKind[] = ['streak', 'rowdiff'];

/** rooms.json `look` (kept here, not in shared/rooms.ts, while that file is being changed elsewhere; the server ignores it). */
export interface RoomLook {
  mode: 'texture' | 'screen' | 'hybrid';
  /**
   * "rgb": 2 bits per channel (64 colours, levels and gamma not used); "grey" or "gb": 4 colours; "pico8": 16 colours,
   * "pico8_32": those and PICO-8's 16 extra colours (nearest, mix, pull only); or sRGB hex colours: 4, darkest first,
   * for the ramp; 2..32 in any order for nearest, mix and pull.
   */
  palette: string | string[];
  /**
   * How a colour becomes a palette colour. ramp: its luminance picks one of the 4 colours in order (a monochrome
   * ramp). channels: R, G and B are dithered separately to 2^bits levels each (palette not used). nearest: the two
   * palette colours nearest in sRGB, dithered by where the colour falls on the line between them. mix: per pixel
   * either the nearest result or the channels result (see `mix`).
   */
  color: 'ramp' | 'channels' | 'nearest' | 'mix';
  /**
   * mix: the share of pixels that take the nearest-palette result, 0..1 (the rest take the channels result). A pixel
   * whose threshold t is below `mix` takes the palette with threshold t / mix, the others the channels with
   * (t - mix) / (1 - mix), so each part has its own full range of thresholds.
   */
  mix: number;
  /**
   * channels: before quantising, move the colour this fraction (0..1) of the way toward the nearest palette colour,
   * then restore its luminance (so it takes the palette colour's hue and saturation, not its brightness).
   */
  pull: number;
  /** channels, mix: bits per channel [r, g, b], 1..8 each ([5, 6, 5] = 16-bit colour, [2, 2, 2] = 64 colours). */
  bits: [number, number, number];
  /** Saturation before quantising: 1 unchanged, 0 grey, 2 twice as far from the grey of the same luminance (sRGB). */
  saturation: number;
  dither: DitherKind;
  /** Ramp: the input luminance (sRGB, 0..1) that maps to the first and the last colour; channels and nearest: applied to each channel. */
  levels: [number, number];
  /** Exponent on the normalised luminance (ramp) or on each normalised channel (channels, nearest); below 1 lifts the darks. */
  gamma: number;
  /** Dither strength, 0 (hard steps) .. 1 (full). */
  spread: number;
  /** texture / hybrid: one pixel on the surfaces, metres. */
  cell_m: number;
  /** texture / hybrid: smallest cell on screen (CSS-independent drawing-buffer px) before cells double. */
  min_px: number;
  /** screen / hybrid: height of the low-res image in pixels. */
  screen_px: number;
  /** halftone: dot spacing; hatch: line spacing; in dither pixels (screen: low-res pixels, texture: cells). */
  period: number;
  /** r2, blue, white, ign, streak, rowdiff: new threshold pattern this many times per second (0 = fixed pattern). */
  dither_fps: number;
  /**
   * streak, rowdiff: length in dither pixels (screen: low-res pixels) of the steps of the row noise, 2..256. The
   * streak threshold is 1-D value noise along each row with a node every `streak` pixels, so the streaks (runs of the
   * lighter colour) are 1 pixel high and of varying length, up to a few times `streak`.
   */
  streak: number;
  /**
   * streak, rowdiff: each row's noise nodes are shifted by a random 0..row_jitter of the node spacing, 0..1 (0: the
   * nodes, where streaks tend to start and end, are at the same x in every row; 1: they do not line up). Each row has
   * its own noise values either way. rowdiff: also scales the random start error of each row.
   */
  row_jitter: number;
  /** rowdiff: the streak noise added to the carried error, in quantisation steps, 0..16 (0 = plain 1-D diffusion). */
  row_noise: number;
  /** screen: the colour of the 3 low-res pixels to the left bleeds into each pixel after quantising, 0..1. */
  smear: number;
  /** screen: the colour (not the brightness) is averaged over 6 low-res pixels along the row after quantising, 0..1. */
  chroma_bleed: number;
  /** screen, hybrid: low-res pixels this many times wider than high, 1..4 (2 = C64-style wide pixels). */
  pixel_aspect: number;
}

const DEFAULTS: RoomLook = { mode: 'texture', palette: 'grey', color: 'ramp', mix: 0.5, pull: 0, bits: [2, 2, 2], saturation: 1, dither: 'bayer4', levels: [0, 1], gamma: 1, spread: 1, cell_m: 0.03, min_px: 2, screen_px: 240, period: 6, dither_fps: 0, streak: 24, row_jitter: 1, row_noise: 0, smear: 0, chroma_bleed: 0, pixel_aspect: 1 };
export const PALETTES: Record<string, string[]> = {
  grey: ['#141414', '#555555', '#a3a3a3', '#ececec'],
  gb: ['#0f380f', '#306230', '#8bac0f', '#9bbc0f'],
  // the PICO-8 fantasy console's 16 colours
  pico8: ['#000000', '#1d2b53', '#7e2553', '#008751', '#ab5236', '#5f574f', '#c2c3c7', '#fff1e8', '#ff004d', '#ffa300', '#ffec27', '#00e436', '#29adff', '#83769c', '#ff77a8', '#ffccaa'],
  // PICO-8's 16 colours and its 16 extra ("secret") colours, numbers 128..143
  pico8_32: ['#000000', '#1d2b53', '#7e2553', '#008751', '#ab5236', '#5f574f', '#c2c3c7', '#fff1e8', '#ff004d', '#ffa300', '#ffec27', '#00e436', '#29adff', '#83769c', '#ff77a8', '#ffccaa',
    '#291814', '#111d35', '#422136', '#125359', '#742f29', '#49333b', '#a28879', '#f3ef7d', '#be1250', '#ff6c24', '#a8e72e', '#00b543', '#065ab5', '#754665', '#ff6e59', '#ff9d81'],
};
/** Palettes (named or listed) that the ramp can use: exactly 4 colours. */
const RAMP_SIZE = 4;
const MAX_PAL = 32;
const HEX = /^#[0-9a-fA-F]{6}$/;

/** Problems with a `look` setting (empty when valid). */
export function lookProblems(v: unknown): string[] {
  const o = v as Partial<RoomLook>;
  if (!o || typeof o !== 'object' || Array.isArray(o)) return ['look: not an object'];
  const p: string[] = [];
  const num = (x: unknown, lo: number, hi: number) => typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi;
  if (!['texture', 'screen', 'hybrid'].includes(o.mode as string)) p.push('look.mode');
  const color = o.color ?? DEFAULTS.color;
  if (!['ramp', 'channels', 'nearest', 'mix'].includes(color)) p.push('look.color');
  // nearest, mix and channels with pull search the palette
  const search = color === 'nearest' || color === 'mix' || (color === 'channels' && typeof o.pull === 'number' && o.pull > 0);
  const pal = typeof o.palette === 'string' ? PALETTES[o.palette] : o.palette;
  const palOk = o.palette === undefined
    || (o.palette === 'rgb' && !search)
    || (Array.isArray(pal) && pal.every((c) => typeof c === 'string' && HEX.test(c))
      && (search ? pal.length >= 2 && pal.length <= MAX_PAL : color === 'ramp' ? pal.length === RAMP_SIZE : true));
  if (!palOk) p.push('look.palette');
  if (o.mix !== undefined && !(num(o.mix, 0, 1) && color === 'mix')) p.push('look.mix');
  if (o.pull !== undefined && !(num(o.pull, 0, 1) && color === 'channels')) p.push('look.pull');
  if (o.bits !== undefined && !(Array.isArray(o.bits) && o.bits.length === 3 && o.bits.every((b) => Number.isInteger(b) && b >= 1 && b <= 8))) p.push('look.bits');
  if (o.saturation !== undefined && !num(o.saturation, 0, 4)) p.push('look.saturation');
  if (o.dither !== undefined && !DITHER_KINDS.includes(o.dither)) p.push('look.dither');
  if (o.levels !== undefined && !(Array.isArray(o.levels) && o.levels.length === 2 && num(o.levels[0], 0, 1) && num(o.levels[1], 0, 1) && o.levels[1] > o.levels[0])) p.push('look.levels');
  if (o.gamma !== undefined && !num(o.gamma, 0.2, 5)) p.push('look.gamma');
  if (o.spread !== undefined && !num(o.spread, 0, 1)) p.push('look.spread');
  if (o.cell_m !== undefined && !num(o.cell_m, 0.002, 1)) p.push('look.cell_m');
  if (o.min_px !== undefined && !num(o.min_px, 0.5, 16)) p.push('look.min_px');
  if (o.screen_px !== undefined && !(num(o.screen_px, 60, 2160) && Number.isInteger(o.screen_px))) p.push('look.screen_px');
  if (o.period !== undefined && !num(o.period, 2, 64)) p.push('look.period');
  if (o.dither_fps !== undefined && !(num(o.dither_fps, 0, 60) && (o.dither_fps === 0 || ANIMATABLE.includes(o.dither ?? DEFAULTS.dither)))) p.push('look.dither_fps');
  // horizontal streak round (docs/plans/dither-2bit.md, Horizontal streak)
  const streaky = STREAKY.includes(o.dither ?? DEFAULTS.dither);
  if (o.streak !== undefined && !(num(o.streak, 2, 256) && streaky)) p.push('look.streak');
  if (o.row_jitter !== undefined && !(num(o.row_jitter, 0, 1) && streaky)) p.push('look.row_jitter');
  if (o.row_noise !== undefined && !(num(o.row_noise, 0, 16) && o.dither === 'rowdiff')) p.push('look.row_noise');
  // row diffusion: a chain of low-res passes in screen mode; the colour must be a per-channel or ramp quantisation
  if (o.dither === 'rowdiff' && !(o.mode === 'screen' && (color === 'ramp' || (color === 'channels' && !(typeof o.pull === 'number' && o.pull > 0))))) p.push('look.dither');
  if (o.smear !== undefined && !(num(o.smear, 0, 1) && o.mode === 'screen')) p.push('look.smear');
  if (o.chroma_bleed !== undefined && !(num(o.chroma_bleed, 0, 1) && o.mode === 'screen')) p.push('look.chroma_bleed');
  if (o.pixel_aspect !== undefined && !(num(o.pixel_aspect, 1, 4) && Number.isInteger(o.pixel_aspect) && o.mode !== 'texture')) p.push('look.pixel_aspect');
  return p;
}

/**
 * The look of `roomId` from the raw rooms file, with defaults; null = none (or invalid: logged). In the dev server
 * `?look=off|texture|screen|hybrid` overrides it, with `look_palette` (rgb, grey, gb, pico8 or hex colours without #,
 * comma separated), `look_dither`, `look_levels` (lo,hi), `look_gamma`, `look_spread`, `look_cell` (m), `look_minpx`, `look_px`, `look_period`, `look_fps`, `look_color`
 * (ramp, channels, nearest, mix), `look_bits` (r,g,b), `look_sat`, `look_mix`, `look_pull`, `look_streak`,
 * `look_jitter` (row_jitter), `look_rownoise`, `look_smear`, `look_bleed` (chroma_bleed) and `look_aspect`
 * (pixel_aspect), for A/B comparisons.
 */
export function roomLook(rooms: { rooms: Record<string, unknown> }, roomId: string): RoomLook | null {
  let raw = (rooms.rooms[roomId] as { look?: unknown } | undefined)?.look;
  if (import.meta.env.DEV) {
    const q = new URLSearchParams(location.search);
    const m = q.get('look');
    if (m === 'off') raw = undefined;
    else if (m) {
      const o: Record<string, unknown> = { ...(raw as object | undefined), mode: m };
      const pal = q.get('look_palette');
      if (pal) o.palette = pal.includes(',') ? pal.split(',').map((h) => `#${h.replace('#', '')}`) : pal;
      const col = q.get('look_color');
      if (col) o.color = col;
      const bits = q.get('look_bits');
      if (bits) o.bits = bits.split(',').map(Number);
      const d = q.get('look_dither');
      if (d) o.dither = d;
      const lv = q.get('look_levels');
      if (lv) o.levels = lv.split(',').map(Number);
      for (const [k, key] of [['look_gamma', 'gamma'], ['look_spread', 'spread'], ['look_cell', 'cell_m'], ['look_minpx', 'min_px'], ['look_px', 'screen_px'], ['look_period', 'period'], ['look_fps', 'dither_fps'], ['look_sat', 'saturation'], ['look_mix', 'mix'], ['look_pull', 'pull'], ['look_streak', 'streak'], ['look_jitter', 'row_jitter'], ['look_rownoise', 'row_noise'], ['look_smear', 'smear'], ['look_bleed', 'chroma_bleed'], ['look_aspect', 'pixel_aspect']] as const) {
        const v = q.get(k);
        if (v) o[key] = Number(v);
      }
      raw = o;
    }
  }
  if (raw === undefined) return null;
  const p = lookProblems(raw);
  if (p.length) {
    console.warn('[look] ignored:', p.join('; '));
    return null;
  }
  return { ...DEFAULTS, ...(raw as Partial<RoomLook>) };
}

/** The palette's colours (the grey ramp for "rgb", which does not use them). */
const paletteOf = (l: RoomLook): string[] => (l.palette === 'rgb' ? PALETTES.grey! : typeof l.palette === 'string' ? PALETTES[l.palette]! : l.palette).slice(0, MAX_PAL);
/** How the colour is quantised: 'rgb' (2 bits per channel, the first colour setting), ramp, channels, nearest or mix. */
const colorKind = (l: RoomLook) => (l.color === 'ramp' && l.palette === 'rgb' ? 'rgb' : l.color);
/** channels with a pull toward the palette. */
const pulls = (l: RoomLook) => l.color === 'channels' && l.pull > 0;
/** The quantisation searches the palette for every pixel (nearest, mix, channels with pull). */
const searchesPalette = (l: RoomLook) => l.color === 'nearest' || l.color === 'mix' || pulls(l);
/** Defines for the quantisation (the ramp without saturation has none: its shader is the one from before colour). */
const colorDefines = (l: RoomLook): Record<string, string> => ({
  ...{ rgb: { LR_RGB: '' }, ramp: {}, channels: { LR_CHANNELS: '' }, nearest: { LR_NEAREST: '' }, mix: { LR_MIX: '' } }[colorKind(l)],
  ...(searchesPalette(l) ? { LR_PALN: String(paletteOf(l).length) } : {}),
  ...(pulls(l) ? { LR_PULL: '' } : {}),
  ...(l.saturation !== 1 ? { LR_SAT: '' } : {}),
});
/**
 * Screen mode quantises into a second low-res image, which a second pass scales up: when the quantisation costs more
 * than the extra pass (palette search, the streak noise), and for row diffusion, smear and bleed.
 */
const quantisesLow = (l: RoomLook) => l.mode === 'screen' && (searchesPalette(l) || l.dither === 'streak' || l.dither === 'rowdiff' || smears(l));
/** smear or chroma_bleed: read the quantised low-res neighbours. */
const smears = (l: RoomLook) => l.smear > 0 || l.chroma_bleed > 0;
/**
 * chroma_bleed (and smear with it) runs in a low-res pass after the quantisation; smear alone runs in the pass that
 * scales the image up (measured: a smear pass cost more than the 3 extra reads per canvas pixel; the bleed's 6 reads
 * and sRGB conversions cost less in the low-res pass).
 */
const postPass = (l: RoomLook) => l.mode === 'screen' && l.chroma_bleed > 0;
const lookKey = (l: RoomLook | null, screenPass: boolean) => (l ? `${screenPass ? 'scr' : l.mode}:${l.dither}:${colorKind(l)}${searchesPalette(l) ? paletteOf(l).length : ''}${pulls(l) ? ':pull' : ''}${l.saturation !== 1 ? ':sat' : ''}` : 'off');

// ---------------------------------------------------------------- GLSL

/** Threshold in [0, 1) for an integer cell `c` (floats holding integers; any sign). lrFrame: 0, or the animation step. */
const GLSL_DITHER = /* glsl */ `
uniform float lrFrame;
uniform float lrPeriod;
#if LR_DITHER == 5
uniform sampler2D lrBlue;
#endif
#if LR_DITHER == 10 || LR_DITHER == 12
uniform float lrStreak;
uniform float lrJitter;
/** Integer hash of two integer-valued floats, in [0, 1). */
float lrHash( vec2 c ) {
  uvec2 q = uvec2( mod( c, 65536.0 ) );
  uint h = q.x * 0x8da6b343u ^ q.y * 0xd8163841u;
  h ^= h >> 16; h *= 0x7feb352du; h ^= h >> 15; h *= 0x846ca68bu; h ^= h >> 16;
  return float( h >> 8 ) / 16777216.0;
}
/**
 * Row noise: 1-D value noise along x with a node every lrStreak pixels (smoothstep between node values hashed from
 * the node and the row, so each row has its own sequence), the row's nodes shifted by a hashed 0..lrJitter of the
 * node spacing, then equalised to a uniform 0..1 distribution (an odd polynomial fitted to the CDF of the
 * interpolated values, error under 0.003), so every tone keeps its share of pixels.
 */
float lrStreakNoise( vec2 c ) {
  float x = c.x / lrStreak + lrHash( vec2( c.y, 7.0 + lrFrame ) ) * lrJitter;
  float i = floor( x ), f = x - i;
  float s = c.y + 1031.0 * lrFrame;
  float v = mix( lrHash( vec2( i, s ) ), lrHash( vec2( i + 1.0, s ) ), f * f * ( 3.0 - 2.0 * f ) ) - 0.5;
  return clamp( 0.5 + v * ( 1.27882 + v * v * ( -0.76234 - 1.34451 * v * v ) ), 0.0, 0.99999 );
}
#endif
float lrThreshold( vec2 c ) {
#if LR_DITHER == 4
  // Bayer 2x2
  ivec2 p = ivec2( mod( c, 2.0 ) );
  return ( float( ( ( p.x ^ p.y ) << 1 ) | p.y ) + 0.5 ) / 4.0;
#elif LR_DITHER == 5
  // blue noise: a 64 x 64 void-and-cluster map (blue-noise.ts), shifted by an R2 step per animation frame
  vec2 o = floor( fract( lrFrame * vec2( 0.7548776662, 0.5698402910 ) ) * 64.0 );
  return ( texelFetch( lrBlue, ivec2( mod( c + o, 64.0 ) ), 0 ).r * 255.0 + 0.5 ) / 256.0;
#elif LR_DITHER == 6
  // white noise: an integer hash of the cell (and the frame)
  uvec2 q = uvec2( mod( c, 65536.0 ) );
  uint h = q.x * 0x8da6b343u ^ q.y * 0xd8163841u ^ uint( lrFrame ) * 0xcb1ab31fu;
  h ^= h >> 16; h *= 0x7feb352du; h ^= h >> 15; h *= 0x846ca68bu; h ^= h >> 16;
  return ( float( h >> 8 ) + 0.5 ) / 16777216.0;
#elif LR_DITHER == 7
  // interleaved gradient noise (Jimenez 2014); animated by moving the pixel 5.588238 per frame
  c = mod( c, 4096.0 ) + 5.588238 * lrFrame;
  return fract( 52.9829189 * fract( dot( c + 0.5, vec2( 0.06711056, 0.00583715 ) ) ) );
#elif LR_DITHER == 8
  // clustered-dot halftone: round dots on a 45 degree screen, lrPeriod pixels apart (cosine spot function)
  vec2 r = vec2( c.x + c.y + 1.0, c.x - c.y ) * ( 0.70710678 / lrPeriod );
  return 1.0 - ( 0.5 + 0.25 * ( cos( 6.2831853 * r.x ) + cos( 6.2831853 * r.y ) ) );
#elif LR_DITHER == 9
  // hatch: diagonal lines lrPeriod pixels apart that grow thicker with the tone (alternating sides, no ties)
  return abs( 2.0 * fract( ( mod( c.x + c.y, 4096.0 ) + 0.25 ) / lrPeriod ) - 1.0 );
#elif LR_DITHER == 10
  // streak: the row noise (1-pixel-high streaks of varying length)
  return lrStreakNoise( c );
#elif LR_DITHER == 11
  // lines: horizontal lines lrPeriod pixels apart that grow thicker with the tone (a line screen at 0 degrees)
  return abs( 2.0 * fract( ( mod( c.y, 4096.0 ) + 0.25 ) / lrPeriod ) - 1.0 );
#elif LR_DITHER == 1
  ivec2 p = ivec2( mod( c, 4.0 ) );
  int z = p.x ^ p.y;
  int v = ( ( z & 1 ) << 3 ) | ( ( p.x & 1 ) << 2 ) | ( z & 2 ) | ( ( p.x & 2 ) >> 1 );
  return ( float( v ) + 0.5 ) / 16.0;
#elif LR_DITHER == 2
  ivec2 p = ivec2( mod( c, 8.0 ) );
  int z = p.x ^ p.y;
  int v = ( ( z & 1 ) << 5 ) | ( ( p.x & 1 ) << 4 ) | ( ( z & 2 ) << 2 ) | ( ( p.x & 2 ) << 1 ) | ( ( z & 4 ) >> 1 ) | ( ( p.x & 4 ) >> 2 );
  return ( float( v ) + 0.5 ) / 64.0;
#elif LR_DITHER == 3
  // R2 low-discrepancy sequence (Roberts 2018): even coverage without the Bayer cross-hatch, close to blue noise
  vec2 q = mod( c, 4096.0 );
  return fract( 0.7548776662 * q.x + 0.5698402910 * q.y + 0.6180339887 * lrFrame );
#else
  return 0.5;
#endif
}
vec3 lrToSrgb( vec3 c ) {
  c = max( c, 0.0 );
  return mix( c * 12.92, 1.055 * pow( c, vec3( 1.0 / 2.4 ) ) - 0.055, step( 0.0031308, c ) );
}
vec3 lrToLinear( vec3 c ) {
  return mix( c / 12.92, pow( ( c + 0.055 ) / 1.055, vec3( 2.4 ) ), step( 0.04045, c ) );
}
uniform vec3 lrPal[ 4 ];
uniform vec2 lrLevels;
uniform float lrGamma;
uniform float lrSpread;
#ifdef LR_SAT
uniform float lrSat;
#endif
#if defined( LR_CHANNELS ) || defined( LR_MIX )
uniform vec3 lrSteps;
/** Each channel to lrSteps levels (0 .. lrSteps - 1), the threshold choosing between the two levels around it (sRGB). */
vec3 lrPickChannels( vec3 s, float t ) {
  vec3 n = lrSteps - 1.0;
  return clamp( floor( s * n + t ), vec3( 0.0 ), n ) / n;
}
#endif
#ifdef LR_PALN
uniform vec3 lrPalS[ LR_PALN ];
/** The two palette colours nearest to s in sRGB (c1 the nearest). */
void lrNearest2( vec3 s, out vec3 c1, out vec3 c2 ) {
  float d1 = 1e9, d2 = 1e9;
  c1 = vec3( 0.0 ); c2 = vec3( 0.0 );
  for ( int i = 0; i < LR_PALN; i ++ ) {
    vec3 e = s - lrPalS[ i ];
    float d = dot( e, e );
    if ( d < d1 ) { d2 = d1; c2 = c1; d1 = d; c1 = lrPalS[ i ]; }
    else if ( d < d2 ) { d2 = d; c2 = lrPalS[ i ]; }
  }
}
/** The two nearest palette colours; the colour's position on the line between them picks one with t (sRGB). */
vec3 lrPickNearest( vec3 s, float t ) {
  vec3 c1, c2;
  lrNearest2( s, c1, c2 );
  vec3 ab = c2 - c1;
  float f = clamp( dot( s - c1, ab ) / max( dot( ab, ab ), 1e-6 ), 0.0, 1.0 );
  return f > t ? c2 : c1;
}
#endif
#ifdef LR_MIX
uniform float lrMix;
#endif
#ifdef LR_PULL
uniform float lrPull;
#endif
/** Linear colour in, linear colour out: 2 bits per channel, the 4-colour ramp, per-channel levels or the nearest
 * palette colours, dithered with threshold t. */
vec3 lrQuantise( vec3 lin, float t ) {
  vec3 s = lrToSrgb( lin );
  t = mix( 0.5, t, lrSpread );
#ifdef LR_SAT
  float ys = dot( s, vec3( 0.2126, 0.7152, 0.0722 ) );
  s = clamp( mix( vec3( ys ), s, lrSat ), 0.0, 1.0 );
#endif
#if defined( LR_CHANNELS ) || defined( LR_NEAREST ) || defined( LR_MIX )
  s = pow( clamp( ( s - lrLevels.x ) / ( lrLevels.y - lrLevels.x ), 0.0, 1.0 ), vec3( lrGamma ) );
#endif
#ifdef LR_MIX
  // t below lrMix: the palette result with threshold t / lrMix; otherwise the channels result with
  // ( t - lrMix ) / ( 1 - lrMix ). Each part keeps a full 0..1 range of thresholds.
  if ( t < lrMix ) return lrToLinear( lrPickNearest( s, t / lrMix ) );
  return lrToLinear( lrPickChannels( s, ( t - lrMix ) / max( 1.0 - lrMix, 1e-6 ) ) );
#elif defined( LR_CHANNELS )
#ifdef LR_PULL
  {
    // toward the nearest palette colour by lrPull, then back to the colour's own luminance
    vec3 c1, c2;
    lrNearest2( s, c1, c2 );
    vec3 q = mix( s, c1, lrPull );
    s = clamp( q + dot( s - q, vec3( 0.2126, 0.7152, 0.0722 ) ), 0.0, 1.0 );
  }
#endif
  return lrToLinear( lrPickChannels( s, t ) );
#elif defined( LR_NEAREST )
  return lrToLinear( lrPickNearest( s, t ) );
#elif defined( LR_RGB )
  s = clamp( floor( s * 3.0 + t ), 0.0, 3.0 ) / 3.0;
  return lrToLinear( s );
#else
  float y = dot( s, vec3( 0.2126, 0.7152, 0.0722 ) );
  float f = pow( clamp( ( y - lrLevels.x ) / ( lrLevels.y - lrLevels.x ), 0.0, 1.0 ), lrGamma ) * 3.0;
  float i = min( floor( f ), 2.0 );
  float k = ( f - i ) > t ? i + 1.0 : i;
  vec3 col = k < 0.5 ? lrPal[ 0 ] : k < 1.5 ? lrPal[ 1 ] : k < 2.5 ? lrPal[ 2 ] : lrPal[ 3 ];
  return col; // lrPal holds linear colours
#endif
}
#if LR_DITHER == 12
/** Row diffusion, first step: the value to diffuse, in quantisation steps (ramp: 0..3 in .r; channels: 0..2^bits-1). */
vec3 lrPrep( vec3 lin ) {
  vec3 s = lrToSrgb( lin );
#ifdef LR_SAT
  float ys = dot( s, vec3( 0.2126, 0.7152, 0.0722 ) );
  s = clamp( mix( vec3( ys ), s, lrSat ), 0.0, 1.0 );
#endif
#if defined( LR_CHANNELS )
  return pow( clamp( ( s - lrLevels.x ) / ( lrLevels.y - lrLevels.x ), 0.0, 1.0 ), vec3( lrGamma ) ) * ( lrSteps - 1.0 );
#elif defined( LR_RGB )
  return s * 3.0;
#else
  float y = dot( s, vec3( 0.2126, 0.7152, 0.0722 ) );
  return vec3( pow( clamp( ( y - lrLevels.x ) / ( lrLevels.y - lrLevels.x ), 0.0, 1.0 ), lrGamma ) * 3.0 );
#endif
}
/** Row diffusion, last step: levels k (whole quantisation steps) to a linear colour. */
vec3 lrLevelColour( vec3 k ) {
#if defined( LR_CHANNELS )
  return lrToLinear( clamp( k, vec3( 0.0 ), lrSteps - 1.0 ) / ( lrSteps - 1.0 ) );
#elif defined( LR_RGB )
  return lrToLinear( clamp( k, 0.0, 3.0 ) / 3.0 );
#else
  float i = k.r;
  return i < 0.5 ? lrPal[ 0 ] : i < 1.5 ? lrPal[ 1 ] : i < 2.5 ? lrPal[ 2 ] : lrPal[ 3 ];
#endif
}
#endif
`;

const MAT_VERT_PARS = /* glsl */ `varying vec3 vLrRetroWorld;\n`;
const MAT_VERT = /* glsl */ `#include <fog_vertex>
vLrRetroWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;`;
const MAT_FRAG_PARS = /* glsl */ `varying vec3 vLrRetroWorld;
uniform float lrCellUv;
uniform float lrCellM;
uniform float lrMinPx;
`;
/** Cell coordinates and level: in the map's UVs (mapped materials) or on the face's dominant world plane. */
const MAT_CELL = /* glsl */ `
vec2 lrCell; float lrLevel; vec2 lrCellCentreUv;
{
#ifdef USE_MAP
  vec2 g = vMapUv / lrCellUv;
#else
  vec3 n = abs( cross( dFdx( vLrRetroWorld ), dFdy( vLrRetroWorld ) ) );
  vec2 w = n.x > n.y && n.x > n.z ? vLrRetroWorld.zy : n.y > n.z ? vLrRetroWorld.xz : vLrRetroWorld.xy;
  vec2 g = w / lrCellM;
#endif
  // cells per screen pixel; cells double until one covers at least lrMinPx pixels
  vec2 fw = fwidth( g );
  lrLevel = clamp( ceil( log2( max( max( fw.x, fw.y ) * lrMinPx, 1e-6 ) ) ), 0.0, 4.0 );
  float s = exp2( lrLevel );
  lrCell = floor( g / s );
#ifdef USE_MAP
  lrCellCentreUv = ( lrCell + 0.5 ) * s * lrCellUv;
#else
  lrCellCentreUv = vec2( 0.0 );
#endif
}
`;
/** The map read once per cell (its average: the mip level of the cell's size). */
const MAT_MAP = /* glsl */ `#ifdef USE_MAP
{
  float lod = log2( max( lrCellUv * exp2( lrLevel ) * float( textureSize( map, 0 ).x ), 1.0 ) );
  vec4 sampledDiffuseColor = textureLod( map, lrCellCentreUv, lod );
  diffuseColor *= sampledDiffuseColor;
}
#endif`;
const MAT_OUT = /* glsl */ `outgoingLight = lrQuantise( outgoingLight, lrThreshold( lrCell ) );
#include <opaque_fragment>`;

const PASS_VERT = /* glsl */ `varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4( position.xy, 0.0, 1.0 ); }`;
const PASS_FRAG = /* glsl */ `varying vec2 vUv;
uniform sampler2D tLow;
uniform vec2 lowSize;
${GLSL_DITHER}
#if defined( LR_RD_BLOCK ) || defined( LR_RD_PREFIX ) || defined( LR_RD_FINAL )
// row diffusion (rowdiff): tVal holds the values in quantisation steps (lowSize), tBlk the block sums or prefixes
uniform sampler2D tVal;
uniform sampler2D tBlk;
uniform float lrRowNoise;
#endif
#if defined( LR_SMEAR ) || defined( LR_BLEED )
uniform float lrSmear;
uniform float lrBleed;
/** The low-res pixel dx to the right of this one (nearest; clamped at the edge). */
vec3 lrAt( float dx ) {
  return texture2D( tLow, ( floor( vUv * lowSize ) + vec2( dx + 0.5, 0.5 ) ) / lowSize ).rgb;
}
#endif
void main() {
#if defined( LR_RD_BLOCK )
  // the sum of a block of LR_RD_N values of the row
  ivec2 p = ivec2( gl_FragCoord.xy );
  int w = int( lowSize.x );
  vec3 s = vec3( 0.0 );
  for ( int i = 0; i < LR_RD_N; i ++ ) {
    int x = p.x * LR_RD_N + i;
    if ( x < w ) s += texelFetch( tVal, ivec2( x, p.y ), 0 ).rgb;
  }
  gl_FragColor = vec4( s, 1.0 );
#elif defined( LR_RD_PREFIX )
  // the sum of the blocks before this one in the row
  ivec2 p = ivec2( gl_FragCoord.xy );
  vec3 s = vec3( 0.0 );
  for ( int i = 0; i < p.x; i ++ ) s += texelFetch( tBlk, ivec2( i, p.y ), 0 ).rgb;
  gl_FragColor = vec4( s, 1.0 );
#elif defined( LR_RD_FINAL )
  // 1-D error diffusion with the whole error carried to the next pixel of the row is the same as rounding the
  // running sum: level_i = round( S_i ) - round( S_i-1 ), S_i = f_0 + .. + f_i (in quantisation steps). The running
  // sum is the block prefix plus up to LR_RD_N values. A per-row start error (row_jitter) and the row noise (row_noise)
  // are added inside the rounding.
  ivec2 p = ivec2( gl_FragCoord.xy );
  vec3 S = texelFetch( tBlk, ivec2( p.x / LR_RD_N, p.y ), 0 ).rgb;
  for ( int x = p.x / LR_RD_N * LR_RD_N; x <= p.x; x ++ ) S += texelFetch( tVal, ivec2( x, p.y ), 0 ).rgb;
  vec3 f = texelFetch( tVal, p, 0 ).rgb;
  vec2 c = vec2( p );
  float r = lrHash( vec2( c.y, 11.0 + lrFrame ) ) * lrJitter;
  float t1 = r + lrRowNoise * ( lrStreakNoise( c ) - 0.5 );
  float t0 = r + lrRowNoise * ( lrStreakNoise( c - vec2( 1.0, 0.0 ) ) - 0.5 );
  vec3 k = floor( S + t1 + 0.5 ) - floor( S - f + t0 + 0.5 );
  gl_FragColor = vec4( lrLevelColour( k ), 1.0 );
  #include <colorspace_fragment>
#else
  vec3 c = texture2D( tLow, vUv ).rgb;
#ifdef LR_QUANTISE
#if LR_DITHER == 12
  // row diffusion, first step: the values in quantisation steps (a float target)
  gl_FragColor = vec4( lrPrep( c ), 1.0 );
  return;
#else
  c = lrQuantise( c, lrThreshold( floor( vUv * lowSize ) ) );
#endif
#endif
#ifdef LR_SMEAR
  // the 3 pixels to the left bleed into this one (weights 0.35, 0.15, 0.1 at smear 1)
  c = c * ( 1.0 - 0.6 * lrSmear ) + lrSmear * ( 0.35 * lrAt( -1.0 ) + 0.15 * lrAt( -2.0 ) + 0.1 * lrAt( -3.0 ) );
#endif
#ifdef LR_BLEED
  {
    // the colour averaged over 6 pixels of the row (4 left, this, 1 right), this pixel's brightness kept (sRGB, Rec. 601 luma)
    vec3 a = vec3( 0.0 );
    for ( int i = -4; i <= 1; i ++ ) a += lrToSrgb( lrAt( float( i ) ) );
    a /= 6.0;
    vec3 sc = lrToSrgb( c );
    vec3 w = vec3( 0.299, 0.587, 0.114 );
    vec3 bl = clamp( a + dot( sc - a, w ), 0.0, 1.0 );
    c = lrToLinear( mix( sc, bl, lrBleed ) );
  }
#endif
  gl_FragColor = vec4( c, 1.0 );
  #include <colorspace_fragment>
#endif
}`;

const DITHER_NUM: Record<DitherKind, number> = { none: 0, bayer4: 1, bayer8: 2, r2: 3, bayer2: 4, blue: 5, white: 6, ign: 7, halftone: 8, hatch: 9, streak: 10, lines: 11, rowdiff: 12 };
/** Row diffusion: values per block in the block-sum pass. */
const RD_BLOCK = 16;

interface Patched {
  mat: MeshBasicMaterial;
  cellUv: { value: number };
}

export class RetroLook {
  private look: RoomLook | null = null;
  /** Uniforms shared by every patched material and the pass (palette, levels, spread, cell size, min px). */
  private readonly u = {
    lrPal: { value: [new Vector3(), new Vector3(), new Vector3(), new Vector3()] },
    lrLevels: { value: new Vector2(0, 1) },
    lrGamma: { value: 1 },
    lrSpread: { value: 1 },
    lrCellM: { value: 0.03 },
    lrMinPx: { value: 2 },
    lrPeriod: { value: 6 },
    lrFrame: { value: 0 },
    lrBlue: { value: null as DataTexture | null },
    lrSat: { value: 1 },
    lrSteps: { value: new Vector3(4, 4, 4) },
    lrPalS: { value: Array.from({ length: MAX_PAL }, () => new Vector3()) },
    lrMix: { value: 0.5 },
    lrPull: { value: 0 },
    lrStreak: { value: 24 },
    lrJitter: { value: 1 },
    lrRowNoise: { value: 0 },
    lrSmear: { value: 0 },
    lrBleed: { value: 0 },
  };
  /** The look changes per frame (dither_fps > 0, and the user has not asked for reduced motion). */
  private fps = 0;
  private patched = new Map<Material, Patched>();
  /** Map UV units per metre, per material (from its meshes' areas). */
  private density = new Map<Material, number>();
  private target: WebGLRenderTarget | null = null;
  private pass: { scene: Scene; camera: OrthographicCamera; mat: ShaderMaterial; blit: { scene: Scene; mat: ShaderMaterial } | null; rd: RowDiffPasses | null; post: PassStep | null } | null = null;
  /** smear, chroma_bleed: the quantised low-res image after the smear pass. */
  private target3: WebGLRenderTarget | null = null;
  /** rowdiff: float images (values, block sums, block prefixes). */
  private rdTargets: { val: WebGLRenderTarget; blk: WebGLRenderTarget; pre: WebGLRenderTarget } | null = null;
  /** nearest, mix, pull: the quantised low-res image (the palette search runs once per low-res pixel, not per canvas pixel). */
  private target2: WebGLRenderTarget | null = null;
  private readonly size = new Vector2();

  get current(): RoomLook | null {
    return this.look;
  }

  /** The material patch is on (texture or hybrid). */
  private get inMaterials(): boolean {
    return !!this.look && this.look.mode !== 'screen';
  }

  /** The low-res pass is on (screen or hybrid). */
  get screenPass(): boolean {
    return !!this.look && this.look.mode !== 'texture';
  }

  /** Set the room's look; materials already patched (a room switch that keeps the scene) recompile when the patch changes. */
  setLook(look: RoomLook | null, s?: LoadedScene): void {
    const before = `${lookKey(this.look, false)}`;
    this.look = look;
    if (look) {
      const pal = paletteOf(look);
      const c = new Color();
      pal.slice(0, RAMP_SIZE).forEach((h, i) => { c.set(h); this.u.lrPal.value[i]!.set(c.r, c.g, c.b); }); // linear
      pal.slice(0, MAX_PAL).forEach((h, i) => { c.set(h).convertLinearToSRGB(); this.u.lrPalS.value[i]!.set(c.r, c.g, c.b); }); // sRGB
      this.u.lrSat.value = look.saturation;
      this.u.lrMix.value = look.mix;
      this.u.lrPull.value = look.pull;
      this.u.lrSteps.value.set(2 ** look.bits[0], 2 ** look.bits[1], 2 ** look.bits[2]);
      this.u.lrLevels.value.set(look.levels[0], look.levels[1]);
      this.u.lrGamma.value = look.gamma;
      this.u.lrSpread.value = look.spread;
      this.u.lrCellM.value = look.cell_m;
      this.u.lrMinPx.value = look.min_px;
      this.u.lrPeriod.value = look.period;
      this.u.lrStreak.value = look.streak;
      this.u.lrJitter.value = look.row_jitter;
      this.u.lrRowNoise.value = look.row_noise;
      this.u.lrSmear.value = look.smear;
      this.u.lrBleed.value = look.chroma_bleed;
      if (look.dither === 'blue' && !this.u.lrBlue.value) {
        const t = new DataTexture(blueNoise64(), 64, 64, RedFormat, UnsignedByteType);
        t.minFilter = t.magFilter = NearestFilter;
        t.generateMipmaps = false;
        t.needsUpdate = true;
        this.u.lrBlue.value = t;
      }
    }
    const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.fps = look && ANIMATABLE.includes(look.dither) && !reduced ? look.dither_fps : 0;
    this.u.lrFrame.value = 0;
    if (s && look && this.inMaterials) this.prepare(s);
    if (before !== lookKey(this.look, false)) for (const p of this.patched.values()) p.mat.needsUpdate = true;
    if (this.pass) {
      this.pass.mat.dispose();
      this.pass.blit?.mat.dispose();
      if (this.pass.rd) for (const m of [this.pass.rd.block.mat, this.pass.rd.prefix.mat, this.pass.rd.final.mat]) m.dispose();
      this.pass.post?.mat.dispose();
      this.pass = null;
    }
    if (this.target3 && !(this.screenPass && look && postPass(look))) {
      this.target3.dispose();
      this.target3 = null;
    }
    if (this.target2 && !(this.screenPass && look && quantisesLow(look))) {
      this.target2.dispose();
      this.target2 = null;
    }
    if (this.rdTargets && !(this.screenPass && look && look.mode === 'screen' && look.dither === 'rowdiff')) {
      for (const t of Object.values(this.rdTargets)) t.dispose();
      this.rdTargets = null;
    }
    if (!this.screenPass && this.target) {
      this.target.dispose();
      this.target = null;
    }
  }

  /** Patch a loaded tier's room materials (before its programs compile). Nothing happens while no look is set. */
  prepare(s: LoadedScene): void {
    if (!this.inMaterials) return;
    this.measure(s);
    for (const mat of s.roomMaterials) {
      if (this.patched.has(mat)) continue;
      const cellUv = { value: 0.01 };
      this.patched.set(mat, { mat, cellUv });
      const prev = mat.onBeforeCompile;
      const prevKey = mat.customProgramCacheKey === Material.prototype.customProgramCacheKey ? '' : mat.customProgramCacheKey();
      mat.onBeforeCompile = (shader, renderer) => {
        prev.call(mat, shader, renderer);
        if (!this.inMaterials || !this.look) return;
        cellUv.value = (this.density.get(mat) ?? 1) * this.look.cell_m;
        Object.assign(shader.uniforms, this.u, { lrCellUv: cellUv });
        shader.defines = { ...shader.defines, LR_DITHER: DITHER_NUM[this.look.dither], ...colorDefines(this.look) };
        shader.vertexShader = MAT_VERT_PARS + shader.vertexShader.replace('#include <fog_vertex>', MAT_VERT);
        shader.fragmentShader = MAT_FRAG_PARS + GLSL_DITHER + shader.fragmentShader
          .replace('#include <map_fragment>', `${MAT_CELL}\n${MAT_MAP}`)
          .replace('#include <opaque_fragment>', MAT_OUT);
      };
      mat.customProgramCacheKey = () => `${prevKey}|retro-${lookKey(this.inMaterials ? this.look : null, false)}`;
      mat.needsUpdate = true;
    }
  }

  /** Map UV units per metre for each textured room material: area-weighted median of the per-triangle ratio. */
  private measure(s: LoadedScene): void {
    const per = new Map<Material, [number, number][]>();
    const a = new Vector3(), b = new Vector3(), c = new Vector3();
    s.root.traverse((o) => {
      const m = o as Mesh;
      if (!m.isMesh) return;
      const mat = m.material as MeshBasicMaterial;
      if (!mat.isMeshBasicMaterial || !mat.map || this.density.has(mat)) return;
      const pos = m.geometry.attributes.position, uv = m.geometry.attributes.uv;
      if (!pos || !uv) return;
      const map = mat.map as Texture;
      map.updateMatrix();
      const e = map.matrix.elements;
      const det = Math.abs(e[0]! * e[4]! - e[1]! * e[3]!) || 1;
      const idx = m.geometry.index;
      const n = idx ? idx.count : pos.count;
      let l = per.get(mat);
      if (!l) per.set(mat, (l = []));
      for (let i = 0; i + 2 < n; i += 3) {
        const i0 = idx ? idx.getX(i) : i, i1 = idx ? idx.getX(i + 1) : i + 1, i2 = idx ? idx.getX(i + 2) : i + 2;
        a.fromBufferAttribute(pos, i0).applyMatrix4(m.matrixWorld);
        b.fromBufferAttribute(pos, i1).applyMatrix4(m.matrixWorld);
        c.fromBufferAttribute(pos, i2).applyMatrix4(m.matrixWorld);
        const wa = b.sub(a).cross(c.sub(a)).length() / 2;
        const u0 = uv.getX(i0), v0 = uv.getY(i0);
        const ua = Math.abs((uv.getX(i1) - u0) * (uv.getY(i2) - v0) - (uv.getX(i2) - u0) * (uv.getY(i1) - v0)) / 2 * det;
        if (wa > 1e-8 && ua > 0) l.push([Math.sqrt(ua / wa), wa]);
      }
    });
    for (const [mat, l] of per) {
      if (!l.length) continue;
      l.sort((x, y) => x[0] - y[0]);
      const half = l.reduce((t, x) => t + x[1], 0) / 2;
      let acc = 0, med = l[0]![0];
      for (const [r, w] of l) { acc += w; if (acc >= half) { med = r; break; } }
      this.density.set(mat, med);
    }
  }

  /** Measured density per patched material (tests, HUD): material name -> map UV per metre. */
  get densities(): Record<string, number> {
    const o: Record<string, number> = {};
    for (const [m, d] of this.density) o[m.name || m.uuid] = +d.toFixed(5);
    return o;
  }

  /** The pattern changes over time: the render loop keeps running while this is true. */
  get animated(): boolean {
    return this.fps > 0;
  }

  /** Advance the animation step for time `now` (ms); true when it changed (the frame needs drawing). */
  tick(now: number): boolean {
    if (!this.fps) return false;
    const f = Math.floor((now / 1000) * this.fps) % 4096;
    if (f === this.u.lrFrame.value) return false;
    this.u.lrFrame.value = f;
    return true;
  }

  /** Draw the frame: straight to the canvas, or through the low-res target and the pass. */
  render(renderer: WebGLRenderer, scene: Scene, camera: Camera): void {
    if (!this.screenPass || !this.look) {
      renderer.render(scene, camera);
      return;
    }
    renderer.getDrawingBufferSize(this.size);
    const h = this.look.screen_px;
    // pixel_aspect: fewer, wider low-res pixels
    const w = Math.max(1, Math.round((h * this.size.x) / Math.max(1, this.size.y) / this.look.pixel_aspect));
    if (!this.target) {
      this.target = new WebGLRenderTarget(w, h, { minFilter: NearestFilter, magFilter: NearestFilter, generateMipmaps: false, depthBuffer: true, samples: 0 });
      this.target.texture.colorSpace = SRGBColorSpace;
    } else if (this.target.width !== w || this.target.height !== h) this.target.setSize(w, h);
    const quantise = this.look.mode === 'screen';
    // nearest, mix and pull search the palette per pixel, rowdiff runs over the rows, smear and bleed read the
    // quantised neighbours: quantise into a second low-res image, then scale that up
    const lowQ = quantisesLow(this.look);
    const rowdiff = quantise && this.look.dither === 'rowdiff';
    if (lowQ) {
      if (!this.target2) {
        this.target2 = new WebGLRenderTarget(w, h, { minFilter: NearestFilter, magFilter: NearestFilter, generateMipmaps: false, depthBuffer: false, samples: 0 });
        this.target2.texture.colorSpace = SRGBColorSpace;
      } else if (this.target2.width !== w || this.target2.height !== h) this.target2.setSize(w, h);
    }
    const post = quantise && postPass(this.look);
    if (post) {
      if (!this.target3) {
        this.target3 = new WebGLRenderTarget(w, h, { minFilter: NearestFilter, magFilter: NearestFilter, generateMipmaps: false, depthBuffer: false, samples: 0 });
        this.target3.texture.colorSpace = SRGBColorSpace;
      } else if (this.target3.width !== w || this.target3.height !== h) this.target3.setSize(w, h);
    }
    if (rowdiff) {
      const bw = Math.ceil(w / RD_BLOCK);
      const ft = (tw: number) => new WebGLRenderTarget(tw, h, { type: FloatType, minFilter: NearestFilter, magFilter: NearestFilter, generateMipmaps: false, depthBuffer: false, samples: 0 });
      if (!this.rdTargets) this.rdTargets = { val: ft(w), blk: ft(bw), pre: ft(bw) };
      else if (this.rdTargets.val.width !== w || this.rdTargets.val.height !== h) {
        this.rdTargets.val.setSize(w, h);
        this.rdTargets.blk.setSize(bw, h);
        this.rdTargets.pre.setSize(bw, h);
      }
    }
    if (!this.pass) {
      const quad = (mat: ShaderMaterial) => {
        const ps = new Scene();
        const m = new Mesh(new PlaneGeometry(2, 2), mat);
        m.frustumCulled = false;
        ps.add(m);
        return ps;
      };
      const passMat = (defines: Record<string, string | number>, extra: Record<string, { value: unknown }> = {}) => new ShaderMaterial({
        vertexShader: PASS_VERT,
        fragmentShader: PASS_FRAG,
        uniforms: { ...this.u, tLow: { value: null }, lowSize: { value: new Vector2(w, h) }, ...extra },
        defines,
        depthTest: false,
        depthWrite: false,
      });
      const mat = passMat({ LR_DITHER: DITHER_NUM[this.look.dither], ...(quantise ? { LR_QUANTISE: '' } : {}), ...colorDefines(this.look) });
      let blit: { scene: Scene; mat: ShaderMaterial } | null = null;
      if (lowQ) {
        const bm = passMat({ LR_DITHER: 0, ...(!post && this.look.smear > 0 ? { LR_SMEAR: '' } : {}) });
        blit = { scene: quad(bm), mat: bm };
      }
      let postStep: PassStep | null = null;
      if (post) {
        // bleed (and smear) once per low-res pixel (target2 -> target3), not per canvas pixel
        const pm = passMat({ LR_DITHER: 0, ...(this.look.smear > 0 ? { LR_SMEAR: '' } : {}), ...(this.look.chroma_bleed > 0 ? { LR_BLEED: '' } : {}) });
        postStep = { scene: quad(pm), mat: pm };
      }
      let rd: RowDiffPasses | null = null;
      if (rowdiff) {
        const step = (k: string) => {
          const m = passMat({ LR_DITHER: DITHER_NUM.rowdiff, LR_RD_N: RD_BLOCK, [k]: '', ...colorDefines(this.look!) }, { tVal: { value: null }, tBlk: { value: null } });
          return { scene: quad(m), mat: m };
        };
        rd = { block: step('LR_RD_BLOCK'), prefix: step('LR_RD_PREFIX'), final: step('LR_RD_FINAL') };
      }
      this.pass = { scene: quad(mat), camera: new OrthographicCamera(-1, 1, 1, -1, 0, 1), mat, blit, rd, post: postStep };
    }
    this.pass.mat.uniforms.tLow!.value = this.target.texture;
    (this.pass.mat.uniforms.lowSize!.value as Vector2).set(w, h);
    renderer.setRenderTarget(this.target);
    renderer.render(scene, camera);
    if (this.pass.rd && this.rdTargets && this.target2) {
      // row diffusion: values -> block sums -> block prefixes -> levels (into target2)
      const { val, blk, pre } = this.rdTargets;
      const rd = this.pass.rd;
      for (const st of [rd.block, rd.prefix, rd.final]) {
        st.mat.uniforms.tVal!.value = val.texture;
        (st.mat.uniforms.lowSize!.value as Vector2).set(w, h);
      }
      rd.block.mat.uniforms.tBlk!.value = null;
      rd.prefix.mat.uniforms.tBlk!.value = blk.texture;
      rd.final.mat.uniforms.tBlk!.value = pre.texture;
      renderer.setRenderTarget(val);
      renderer.render(this.pass.scene, this.pass.camera);
      renderer.setRenderTarget(blk);
      renderer.render(rd.block.scene, this.pass.camera);
      renderer.setRenderTarget(pre);
      renderer.render(rd.prefix.scene, this.pass.camera);
      renderer.setRenderTarget(this.target2);
      renderer.render(rd.final.scene, this.pass.camera);
    } else if (this.pass.blit && this.target2) {
      renderer.setRenderTarget(this.target2);
      renderer.render(this.pass.scene, this.pass.camera);
    }
    let low = this.target2;
    if (this.pass.post && this.target2 && this.target3) {
      this.pass.post.mat.uniforms.tLow!.value = this.target2.texture;
      (this.pass.post.mat.uniforms.lowSize!.value as Vector2).set(w, h);
      renderer.setRenderTarget(this.target3);
      renderer.render(this.pass.post.scene, this.pass.camera);
      low = this.target3;
    }
    if (this.pass.blit && low) {
      this.pass.blit.mat.uniforms.tLow!.value = low.texture;
      (this.pass.blit.mat.uniforms.lowSize!.value as Vector2).set(w, h);
      renderer.setRenderTarget(null);
      renderer.render(this.pass.blit.scene, this.pass.camera);
      return;
    }
    renderer.setRenderTarget(null);
    renderer.render(this.pass.scene, this.pass.camera);
  }
}

type PassStep = { scene: Scene; mat: ShaderMaterial };
/** rowdiff's three passes after the value pass. */
interface RowDiffPasses {
  block: PassStep;
  prefix: PassStep;
  final: PassStep;
}
